/* Dynamic Plot code copyright 2025 (c) Tuva Labs Inc. */
import { PlotElement } from "./plotElement";
import ContextMenuUtil from "../../../utils/ContextMenuUtil";

/**
 * Ruler Element: model and controllers for ruler widgets on either the X- or Y-axis.
 * Added to Dyn.PlotShaper's list of plot elements, to make a plot.
 * created by cmiller
 */

class Ruler1DModel {
    constructor( Dyn) {
        this.c1 = new Dyn.axisUtil.NACoord( 0.2 ); // start coordinate of the ruler, defaults to 2/10 of axis length
        this.c2 = new Dyn.axisUtil.NACoord( 0.8 ); // end coordinate of the ruler, defaults to 8/10 of axis length
        this.thickness = 0.6; // thickness on cross-axis, normalized to [0-1], defaults to 6/10 of cross-axis length
        this.isAbs = false; // show absolute value of distance?
        this.axisAttributeID = ''; // if non-empty, track ruler endpoints in the attribute's coordinates
        this.color = this.defaultColor( Dyn );
    }

    defaultColor( Dyn ) {
        return Dyn.plotUtil.refLineColors[1];
    }

    /** swap the start and end coordinates of the ruler, to reverse the direction. */
    flip() {
        [this.c1, this.c2] = [this.c2, this.c1];
    }

    setColor( color ) {
        this.color = color;
    }

    setIsAbs( isAbs ) {
        this.isAbs = Boolean( isAbs );
    }

    save() {
        return { // saveData for this.restore()
            c1: this.c1.save(),
            c2: this.c2.save(),
            thickness: this.thickness,
            isAbs: this.isAbs,
            axisAttributeID: this.axisAttributeID,
            color: this.color,
        };
    }

    restore( saveData, Dyn ) {
        this.c1 = Dyn.axisUtil.NACoord.restore( saveData.c1 );
        this.c2 = Dyn.axisUtil.NACoord.restore( saveData.c2 );
        this.thickness = saveData.thickness;
        this.isAbs = Boolean( saveData.isAbs );
        this.axisAttributeID = saveData.axisAttributeID || '';
        this.color = saveData.color || this.defaultColor( Dyn );
    }

    /** Set the thickness of the ruler, based on the new view coordinate and axis views. */
    setRulerThickness( newViewCoord, xAxisView, yAxisView, isOnX, Dyn ) {
        let axisThickness = isOnX ? yAxisView.bounds.height : xAxisView.bounds.width,
            newThicknessViewCoords = isOnX ?
                (yAxisView.bounds.bottom() - newViewCoord)  :
                (newViewCoord - xAxisView.bounds.left()),
            newThicknessNormalizedCoords = newThicknessViewCoords * Dyn.mathUtil.invertSafely( axisThickness ),
            minThicknessInViewCoords = 0.9 * Dyn.preferences.getFontSize(), // to keep 1/2 of arrow visible, see rvd.arrowThickness
            minThicknessNormalizedCoords = minThicknessInViewCoords * Dyn.mathUtil.invertSafely( axisThickness );
        this.thickness = Dyn.mathUtil.clipToMinMax( minThicknessNormalizedCoords, 1, newThicknessNormalizedCoords ); // clip to [0,1] range
    }

    /** Get the ruler view data: coordinates, rectangles, paths, etc., to draw it on the plot. */
    getRulerViewData( xAxisView, yAxisView, isOnX, Dyn ) {
        let rvd = {
                axisView: isOnX ? xAxisView : yAxisView,
                color: this.color,
                clipRectString: Dyn.plotView.getPlotInteriorClipRect(),
                arrowThickness: 0.9 * Dyn.preferences.getFontSize(), // arrow head thickness+length about 9/10 of the font size
                shaftThickness: 0.3 * Dyn.preferences.getFontSize(), // shaft thickness about 1/3 of the font size
            },
            // get the start and end coordinates in view coordinates, also anchor the ruler to the axis attribute, if any
            v1 = this.c1.getViewCoord( rvd.axisView ),
            v2 = this.c2.getViewCoord( rvd.axisView );
        rvd.string = this.rulerValueToString( rvd.axisView, Dyn ); // note: requires getViewCoord() to be called first
        if( isOnX ) {
            let h = (this.thickness * yAxisView.bounds.height),
                y0 = yAxisView.bounds.bottom() - h,
                r = new Dyn.mathUtil.Rect( v1, y0, v2-v1, h ).normalize();
            rvd.line1Path = Dyn.plotUtil.makeStraightLinePath( v1, r.bottom(), v1, r.top() );
            rvd.line2Path = Dyn.plotUtil.makeStraightLinePath( v2, r.bottom(), v2, r.top() );
            rvd.textCenter = { x: (v1 + v2) / 2, y: r.top() + rvd.arrowThickness };
            rvd.rCoord = r.top(); // ruler location on the cross-axis
            rvd.rRect = r;
        } else {
            // Y-axis ruler
            let w = this.thickness * xAxisView.bounds.width,
                x0 = xAxisView.bounds.left(),
                r = new Dyn.mathUtil.Rect( x0, v1, w, v2-v1 ).normalize();
            rvd.line1Path = Dyn.plotUtil.makeStraightLinePath( r.left(), v1, r.right(), v1 );
            rvd.line2Path = Dyn.plotUtil.makeStraightLinePath( r.left(), v2, r.right(), v2 );
            rvd.textCenter = { y: (v1 + v2) / 2, x: r.right() - rvd.arrowThickness };
            rvd.rCoord = r.right(); // ruler location on the cross-axis
            rvd.rRect = r;
        }
        rvd.arrowPath = this.makeArrowPath( rvd, v1, v2, isOnX, Dyn );
        return rvd;
    }

    /** get the numeric value of a draggable axis location, with the precision tied to the
     * current scale of the axis, so that a 1-pixel change in position shows a change in value.
     * Used for reference line locations, ruler endpoints, divider locations, etc.
     * Compare to Attribute.axisValueToString() - uses attribute precision
     * RefLineBaseElement._valueToString() - uses one pixel precision, but absolute units, not relative units
     * DividerElement.dividerLocationToString() - uses one pixel precision, but absolute units, not relative units
     */
    rulerValueToString( axisView, Dyn ) {
        let string = { value: "", c1:"", c2:"" };
        let v = this.isAbs ? Math.abs(this.c2.a - this.c1.a) : ( this.c2.a - this.c1.a );
        if( axisView.isNumericalScaleAxis() ) {
            var attr          = axisView.getFormattingAttribute(),
                onePixelScale = Math.abs( Dyn.mathUtil.invertSafely( axisView.coordMap.scale ) ),
                options = { precision:Dyn.preferences.getPrecision() }; // add user precision, if any
            v = Dyn.mathUtil.roundToScale( v, onePixelScale, options );
            string.value = attr.axisValueToStringRelativeUnits( v, options.precision ); // NOTE: don't add trailing zeros, unlike axisValueToString()
            string.c1 = attr.axisValueToString( this.c1.a, options.precision );
            string.c2 = attr.axisValueToString( this.c2.a, options.precision );
        }
        return string; // no value for non-numerical axis
    }

    makeArrowPath( rvd, v1, v2, isOnX, Dyn ) {
        // create an arrow path for the ruler, from the Ruler View Data (rvd) and the start and end coordinates v1, v2
        const a = rvd.arrowThickness,
            b = rvd.shaftThickness, // shaft thickness is fraction of height of arrow
            d = isOnX ? rvd.rRect.top()+a : rvd.rRect.right()-a, // distance from axis to ruler midline
            w1 = Math.min( v1, v2 ),
            w2 = Math.max( v1, v2 ),
            // sp = ( v2 > v1 ) ? 1 : -1, // sign of the arrow direction
            sn = ( v2 > v1 ) ? -1 : 1;
        const doubleArrow = [
            [w1, w1+a, w1+a, w2-a, w2-a, w2, w2-a, w2-a, w1+a, w1+a, w1], // x coord if horizontal ruler
            [d, d+a, d+b, d+b, d+a, d, d-a, d-b, d-b, d-a, d]]; // y coords if vertical ruler
        const singleArrow = [
            [v1, v2+a*sn, v2+a*sn, v2, v2+a*sn, v2+a*sn, v1, v1], // x coord if horizontal ruler
            [d+b, d+b, d+a, d, d-a, d-b, d-b, d+b]]; // y coords if vertical ruler
        let shape = this.isAbs ? doubleArrow : singleArrow; // use doubleArrow arrow for absolute value, singleArrow arrow for signed value
        return makePath( shape, isOnX, Dyn );

        function makePath( shape, isOnX, Dyn ) {
            let path = '';
            Dyn.assert( shape[0].length === shape[1].length );
            const iLast = shape[0].length - 1; // last index of the shape arrays
            shape[0].forEach( (xi, i)=> {
                let yi = shape[1][i], // y coordinate for the arrow
                    op = (i === 0) ? 'M' : 'L'; // move to first point, then line to others
                if( i === iLast ) {
                    path += 'Z'; // close the path at the end
                } else if ( isOnX ) {
                    path += op + Math.round(xi) + ',' + Math.round(yi) + ' ';
                } else {
                    path += op + Math.round(yi) + ',' + Math.round(xi) + ' ';
                }
            });
            return path.trim(); // return the path string
        }
    }

    /**
     * Get the positions of the close box and context menu button for the ruler.
     * @param refBBox - textRectEl or arrowPath.getBBox()
     * @param iconSize - view size of square buttons, in pixels
     * @param isOnX - ruler on X-axis or Y-axis?
     * @returns {*[]}
     */
    getButtonPositions( refBBox, iconSize, isOnX ) {
        let i = iconSize, c, m, margin;
        if( isOnX ) {
            let x = refBBox.x2, y = refBBox.y; // top-right side of the text rectangle (or arrow path bbox if no text)
            c = [ x+(i*1.95), y+(i*-0.2) ]; // closebox on X
            m = [ x+(i*0.75), y+(i*-0.2) ]; // context menu button on X
            margin = i* 2.5; // expanded clip area so buttons are visible even if overlapping the legend/title areas
        } else {
            let x = refBBox.x2, y = refBBox.y;// top-right side of the text rectangle (or arrow path bbox if no text)
            c = [ x+(i*1.95), y+(i*-0.2) ]; // closebox on X
            m = [ x+(i*0.75), y+(i*-0.2) ]; // context menu button on X
            margin = i* 2.5; // expanded clip area so buttons are visible even if overlapping the legend/title areas
        }
        return {
            closeBox: { x: c[0], y: c[1] },
            contextMenuButton: { x: m[0], y: m[1] },
            clipAreaMargin: margin
        };
    }
}

/** base class for a ruler element, which can be on the X or Y axis.
 * By the default rules for class PlotElement, we only allow one object of each
 * subclass in the plot at a time: one X-axis ruler, one Y-axis ruler. */
class RulerBaseElement extends PlotElement{

    constructor( Dyn ) {
        super(Dyn);
        this.model = new Ruler1DModel( Dyn ); // saved model for the ruler
        this.axisAttributeID = "";  // if non-empty, track ruler endpoints in the attribute's coordinates
        this.isSelected = false;
    }

    save() {
        let plotElementModel = super.save(); // save the base model
        plotElementModel.model = this.model.save(); // save the ruler model
        return plotElementModel;
    }

    restore( savedData ) {
        super.restore( savedData ); // restore base model
        if( savedData.model ) {
            this.model.restore( savedData.model, this.dyn );
        }
    }

    get kUndoAction(){ return 'Ruler'; }
    get setID() { return 'ruler'; } // ID for the set of adornments for this ruler

    /**
     * Restore the plot element during a SwapAxis, to swap the ruler from one axis to another
     * @param savedData
     */
    swapAxis( savedData ) {
        let Dyn = this.dyn,
            swappedType = this.isOnX ? Dyn.EPlotControl.kControlsRulerY : Dyn.EPlotControl.kControlsRulerX;
        return [ this.primaryType, swappedType, this.save()];
    }

    /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
    calcAxis( ioWhatChanged, subViewRef) {
        // update the axis attribute ID, and possibly strip the attribute coordinates in NACoord,
        // to maintain the relative position of the ruler lines on the axis when the attribute changes,
        // yet track the attribute value when the axis endpoints change.
        let Dyn = this.dyn,
            axisView = this.isOnX ? subViewRef.xAxisView : subViewRef.yAxisView,
            result = Dyn.axisUtil.isNewAttributeCoord( this.axisAttributeID, axisView, this.isNewlyRestoredData );
        if( result.needToStripCoords ) {
            this.model.c1.stripAttributeCoord();
            this.model.c2.stripAttributeCoord();
        }
        this.axisAttributeID = result.newAttributeID;
    }

    /** Get the Raphael/SVG element for a part of the ruler, in the associated adornment array. */
    getAdornmentsEl( rulerAdorns, partName ) {
        const rulerAdornmentArrayNames = [ 'contextMenuButton', 'closeBox', 'rulerSet' ]; // in push order
        const index = rulerAdornmentArrayNames.indexOf( partName );
        this.dyn.assertArrayIndex( rulerAdornmentArrayNames, index, 'getAdornmentsEl() invalid part name: '+partName );
        return( rulerAdorns?.[index]); // return the Raphael selement/set, may be null if not created yet
    }

    /**
     * Get the Raphael/SVG element for a part of the ruler adornment set.
     */
    getSetEL( rulerSet, partName ) {
        const rulerSetNames = [ 'line1', 'line2', 'cover1', 'cover2', 'arrowPathEl', 'textRectEl', 'textEl' ]; // in push order
        const index = rulerSetNames.indexOf( partName );
        this.dyn.assertArrayIndex( rulerSetNames, index, 'getSetEL() invalid part name: '+partName );
        return( rulerSet?.[index]); // return the Raphael element, may be null if not created yet
    }

    /**
     * Update adornments (specific to this element) to show a ruler widget on the plot.
     */
    calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
        let Dyn = this.dyn,
            plotViewPaper = this.plotShaper.adornments.paper,
            rulerAdorns = ioAdornArray;

        // get any existing adornments (Raphael/SVG elements)
        let closeBox = this.getAdornmentsEl( rulerAdorns, 'closeBox' ),
            contextMenuButton = this.getAdornmentsEl( rulerAdorns, 'contextMenuButton' ),
            rulerSet = this.getAdornmentsEl( rulerAdorns, 'rulerSet' );

        // create the adornments all at once if they don't exist
        if( !closeBox ) {
            contextMenuButton = this.makeContextMenuButton().hide();
            closeBox = this.makeCloseBox().hide();
            rulerSet = plotViewPaper.set();
            this.makeLineAdornments( rulerSet, xAxisView, yAxisView );
            this.makeRulerAdornments( rulerSet, xAxisView, yAxisView );
            rulerAdorns.push( contextMenuButton, closeBox, rulerSet );
        }

        // update the adornments
        let rvd = this.model.getRulerViewData( xAxisView, yAxisView, this.isOnX, Dyn );
        this.updateLineAdornments( rulerSet, rvd );
        this.updateRulerAdornments( rulerSet, rvd );
        this.updateMeasures( rvd );
    }

    /** make the lines of the ruler, perpendicular to the axis, and the covers for dragging. */
    makeLineAdornments( rulerSet, xAxisView, yAxisView ) {
        let Dyn = this.dyn,
            plotElement = this,
            plotViewPaper = this.plotShaper.adornments.paper,
            viewOnly = Dyn.preferences.isViewOnly();
        Dyn.assert( rulerSet );

        const cursorLine = this.isOnX ? Dyn.plotUtil.cursorHorizontalBothSide : Dyn.plotUtil.cursorVerticalBothSide;
        const coverWidth = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.refLineCoverWidth );

        let line1 = plotViewPaper.path( 'M0,0' ).attr( { 'stroke-width': Dyn.plotUtil.rulerLineWidth } )
            .data( 'line', 'c1' );
        let line2 = plotViewPaper.path( 'M0,0' ).attr( { 'stroke-width': Dyn.plotUtil.rulerLineWidth } )
            .data( 'line', 'c2' );
        line1.node.setAttribute( 'stroke-dasharray', Dyn.plotUtil.rulerLineDashArray );
        line2.node.setAttribute( 'stroke-dasharray', Dyn.plotUtil.rulerLineDashArray );
        let cover1 = plotViewPaper.path( 'M0,0' ).attr( {
            'stroke-width': coverWidth, fill: "none", stroke: "white",
            opacity: Dyn.plotUtil.opacityInvisibleButDraggable, cursor: cursorLine
        } );
        let cover2 = plotViewPaper.path( 'M0,0' ).attr( {
            'stroke-width': coverWidth, fill: "none", stroke: "white",
            opacity: Dyn.plotUtil.opacityInvisibleButDraggable, cursor: cursorLine
        } );
        line1.data( "focus", cover1 );
        line2.data( "focus", cover2 );

        // save the Raphael elements in the rulerSet, so we can update them later
        rulerSet.push( line1, line2, cover1, cover2 );
        Dyn.assert( cover2 === plotElement.getSetEL( rulerSet, 'cover2', 'push() and getRulerEL() mismatch' ) );

        if( viewOnly ) {
            Dyn.plotUtil.disableMouseOverEvents( line1 );
            Dyn.plotUtil.disableMouseOverEvents( line2 );
            Dyn.plotUtil.disableMouseOverEvents( cover1 );
            Dyn.plotUtil.disableMouseOverEvents( cover2 );
        } else {
            cover1.drag( dragLineMove.bind( line1 ), dragLineStart.bind( line1 ), dragLineEnd.bind( line1 ) );
            cover2.drag( dragLineMove.bind( line2 ), dragLineStart.bind( line2 ), dragLineEnd.bind( line2 ) );
            cover1.hover( lineHoverIn.bind( line1 ), lineHoverOut.bind( line1 ) );
            cover2.hover( lineHoverIn.bind( line2 ), lineHoverOut.bind( line2 ) );
            let ariaLabel1 = Dyn.loc( "Ruler line {{n}} on {{axis}}", { n: 1, axis: plotElement.isOnX ? 'X' : 'Y' } ),
                ariaLabel2 = Dyn.loc( "Ruler line {{n}} on {{axis}}", { n: 2, axis: plotElement.isOnX ? 'X' : 'Y' } );
            plotElement.addKeyHandlers( cover1, ariaLabel1, dragLineMove.bind( line1 ), dragLineStart.bind( line1 ), dragLineEnd.bind( line1 ) );
            plotElement.addKeyHandlers( cover2, ariaLabel2, dragLineMove.bind( line2 ), dragLineStart.bind( line2 ), dragLineEnd.bind( line2 ) );
        }
        return;

        function dragLineStart( x, y, event ) {
            let lineElem = this,
                path = lineElem.attr("path");
            this.data( 'dragUndoData', plotElement.save());
            this.data( "x0", path[0][1] );
            this.data( "y0", path[0][2] );
            highlightLine( event, this );
            plotElement.selectAdornment();
            Dyn.mediator.publish( "plot:updateAdornment", { adornmentSelected:true } ); // to update selection in Summary View
        }
        function dragLineMove( dx, dy, x, y ) {
            // move the ruler start or end line by dragging
            let lineElem = this,
                model = plotElement.model,
                naCoord = model[ lineElem.data("line") ];
            this.data("drag",true); // not dragging until we move
            if( plotElement.isOnX ) {
                let newX = this.data("x0") + dx;
                naCoord.updateFromViewCoord( newX, xAxisView, true, Dyn.plotUtil.refLineSnapDistance );
            } else {
                let newY = this.data("y0") + dy;
                naCoord.updateFromViewCoord( newY, yAxisView, true, Dyn.plotUtil.refLineSnapDistance );
            }
            let rvd = model.getRulerViewData( xAxisView, yAxisView, plotElement.isOnX, plotElement.dyn );
            plotElement.updateLineAdornments( rulerSet, rvd );
            plotElement.updateRulerAdornments( rulerSet, rvd );
            highlightLine( event, this );
        }
        function dragLineEnd( event ) {
            if( ! (this.data( "hover") || event.type === 'keydown')) { // only if not hovering or move with arrow keys
                unhighlightLine( event, this );
            }
            plotElement.makeDragUndoStep( this.data("drag"), this.data('dragUndoData') );
            this.data("drag",false);
            this.data("dragUndoData",null);
            Dyn.plotUtil.setAsKeyboardFocus( this.data("focus")); // refocus on the line for keyboard dragging
        }
        function lineHoverIn( event ) {
            this.data("hover",true);
            highlightLine( event, this );
        }
        function lineHoverOut( event ) {
            this.data("hover",false);
            if( ! this.data("drag")) { // only if neither dragging nor touching
                unhighlightLine( event, this );
            }
        }
        function highlightLine( event, element ) {
            // highlight for hover, drag, touch, or arrow key move
            if( element.data("tooltip") ) {
                if( Dyn.toolTip.isMyElement( this )) {
                    Dyn.toolTip.update( element, event, {} );
                } else {
                    Dyn.toolTip.show( element, event, {} );
                }
            }
            plotElement.addGlow( element, plotElement.model.color, Dyn.plotUtil.lineGlowWidth );
        }
        function unhighlightLine( event, element ) {
            // unhighlight for hover, drag, touch, or arrow key move
            if( Dyn.toolTip.isMyElement( element )) {
                Dyn.toolTip.hide();
            }
            plotElement.removeGlow();
        }
    }   // makeLineAdornments()

    updateLineAdornments( rulerSet, rvd /* ruler view data */ ) {
        let line1 = this.getSetEL( rulerSet, 'line1' ), // visible lines
            line2 = this.getSetEL( rulerSet, 'line2' ),
            cover1 = this.getSetEL( rulerSet, 'cover1' ), // invisible wider cover lines for drag/touch
            cover2 = this.getSetEL( rulerSet, 'cover2' );

        line1.attr( { path: rvd.line1Path, stroke: rvd.color, 'clip-rect':rvd.clipRectString } )
            .data("tooltip", rvd.string.c1 ); // save the tooltip text for the start line
        line2.attr( { path: rvd.line2Path, stroke: rvd.color, 'clip-rect':rvd.clipRectString } )
            .data("tooltip", rvd.string.c2 ); // save the tooltip text for the end line
        cover1.attr( { path: rvd.line1Path, 'clip-rect':rvd.clipRectString } );
        cover2.attr( { path: rvd.line2Path, 'clip-rect':rvd.clipRectString } );
    }

    /** make the visible parts of the ruler parallel to the axis: arrow, text rectangle, and text. */
    makeRulerAdornments( rulerSet, xAxisView, yAxisView ) {
        let Dyn = this.dyn,
            plotElement = this,
            plotViewPaper = this.plotShaper.adornments.paper,
            viewOnly = Dyn.preferences.isViewOnly();
        Dyn.assert( rulerSet );
        const cursorRuler = this.isOnX ? Dyn.plotUtil.cursorVerticalBothSide : Dyn.plotUtil.cursorHorizontalBothSide;

        let arrowPathEl = plotViewPaper.path('M0,0')
            .attr({ stroke:'none', opacity:Dyn.plotUtil.rulerArrowOpacity, cursor:cursorRuler });
        let textRectEl = plotViewPaper.rect(0, 0, 0, 0, Dyn.plotUtil.statLabelRoundRectRadius)
            .attr({ fill: 'white', cursor:cursorRuler });
        let textEl = plotViewPaper.text(0, 0, '')
            .attr({ "font-family":Dyn.plotUtil.statLabelFontFamily });
        arrowPathEl.data( "focus", arrowPathEl );
        textRectEl.data( "focus", arrowPathEl );
        rulerSet.push( arrowPathEl, textRectEl, textEl );
        Dyn.assert( textEl === plotElement.getSetEL( rulerSet, 'textEl', 'push() and getRulerEL() mismatch' ));

        Dyn.plotUtil.disableMouseOverEvents( textEl );
        if( viewOnly ) {
            Dyn.plotUtil.disableMouseOverEvents( textRectEl );
            Dyn.plotUtil.disableMouseOverEvents( arrowPathEl );
        } else {
            let move = dragRulerMove.bind(textRectEl), start = dragRulerStart.bind(textRectEl), end = dragRulerEnd.bind(textRectEl);
            arrowPathEl.attr({ cursor: cursorRuler });
            textEl.attr({ cursor: cursorRuler });
            arrowPathEl.drag( move, start, end );
            textRectEl.drag( move, start, end );
            textRectEl.hover( rulerHoverIn, rulerHoverOut );
            arrowPathEl.hover( rulerHoverIn, rulerHoverOut );
            let ariaLabel = Dyn.loc("Ruler on {{axis}}", { axis: plotElement.isOnX ? 'X':'Y' });
            plotElement.addKeyHandlers( arrowPathEl, ariaLabel, move, start, end );
            plotElement.addKeyHandlers( textRectEl, ariaLabel, move, start, end );
        }
        return;

        function rulerGlowElements() { return [ arrowPathEl, textRectEl ]; }

        function dragRulerStart( x, y, event ) {
            let textRectEl = this;
            textRectEl.data( 'dragUndoData', plotElement.save());
            textRectEl.data( "r0", textRectEl.data("rCoord")); // fix the view coord of ruler for drag
            plotElement.addGlow( rulerGlowElements(), plotElement.model.color, Dyn.plotUtil.lineGlowWidth );
            plotElement.selectAdornment();
            Dyn.mediator.publish( "plot:updateAdornment", { adornmentSelected:true } ); // to update selection in Summary View
        }
        function dragRulerMove( dx, dy, x, y ) {
            // change the ruler thickness by dragging
            let textRectEl = this,
                model = plotElement.model,
                rNew = textRectEl.data("r0") + (plotElement.isOnX ? dy : dx); // new ruler coordinate on the cross-axis
            this.data("drag",true); // not dragging until we move
            model.setRulerThickness( rNew, xAxisView, yAxisView, plotElement.isOnX, plotElement.dyn);
            let rvd = model.getRulerViewData( xAxisView, yAxisView, plotElement.isOnX, plotElement.dyn );
            plotElement.updateLineAdornments( rulerSet, rvd );
            plotElement.updateRulerAdornments( rulerSet, rvd );
            plotElement.addGlow( rulerGlowElements(), plotElement.model.color, Dyn.plotUtil.lineGlowWidth );
        }
        function dragRulerEnd( event ) {
            if( ! this.data( "hover")) {
                if ( Dyn.toolTip.isMyElement( this )) {
                    Dyn.toolTip.hide();
                }
                plotElement.removeGlow();
            }
            plotElement.makeDragUndoStep( this.data("drag"), this.data('dragUndoData') );
            this.data("drag",false);
            this.data("dragUndoData",null);
            Dyn.plotUtil.setAsKeyboardFocus( this.data("focus")); // refocus on the arrow path for keyboard dragging
        }
        function rulerHoverIn( event ) {
            this.data("hover",true);
            if( this.data("tooltip") ) {
                Dyn.toolTip.show( this, event, {} );
            }
            let glowElements = rulerGlowElements();
            Dyn.assert( glowElements[0] === plotElement.getSetEL( rulerSet, 'arrowPathEl' ));
            plotElement.addGlow( glowElements, plotElement.model.color, Dyn.plotUtil.lineGlowWidth );
        }
        function rulerHoverOut( e ) {
            this.data("hover",false);
            if( ! this.data("drag")) { // only if neither dragging nor touching
                if ( Dyn.toolTip.isMyElement( this )) {
                    Dyn.toolTip.hide();
                }
                plotElement.removeGlow();
            }
        }
    } // makeRulerAdornments()

    /** update the arrow path, text rectangle, and text elements of the ruler. */
    updateRulerAdornments( rulerSet, rvd /* ruler view data */ ) {
        let Dyn = this.dyn,
            arrowPathEl = this.getSetEL( rulerSet, 'arrowPathEl' ), // arrow for ruler
            textRectEl = this.getSetEL( rulerSet, 'textRectEl' ), // text rectangle for ruler
            textEl = this.getSetEL( rulerSet, 'textEl' ); // text for ruler
        let hasRulerText = (rvd.string.value.length > 0);

        arrowPathEl.attr( { path: rvd.arrowPath, fill: rvd.color, 'clip-rect':rvd.clipRectString } );
        textEl.attr( { text: rvd.string.value, x: rvd.textCenter.x, y: rvd.textCenter.y, fill: '#000',
            "font-size":Dyn.preferences.getFontSize(), 'clip-rect':rvd.clipRectString } );
        let tbox = new Dyn.mathUtil.Rect( textEl.getBBox() )
            .expand( Dyn.plotUtil.statLabelBorderDx, Dyn.plotUtil.statLabelBorderDy );
        textRectEl.attr( { x: tbox.x, y: tbox.y, width: tbox.width, height: tbox.height, stroke: rvd.color, 'clip-rect':rvd.clipRectString })
            .data( "rCoord", rvd.rCoord ) // save the ruler coordinate for dragging
            .data( "hasRulerText", hasRulerText );
        Dyn.plotUtil.showOrHideElement( textEl, hasRulerText );
        Dyn.plotUtil.showOrHideElement( textRectEl, hasRulerText );
        this.updateSelectionVisuals();
    }

    /** create an undo/redo step if we finished dragging the ruler line. */
    makeDragUndoStep( wasDragged, dragUndoData ) {
        if( !wasDragged || !dragUndoData ) {
            return; // no undo step if not dragged or no data
        }
        let Dyn = this.dyn,
            restoreData = this.save();
        Dyn.mediator.publish( "plot:updateAdornment", {
            saveData: dragUndoData, // to create an undo step
            restoreData: restoreData,
            actionString: Dyn.loc( "Move Ruler on {{axis}}", { axis: this.isOnX ? 'X':'Y' } ),
            updatePlot: true, // to update location in Summary View
            canUpdateCallBack:// update the last undo step only if dragging the same line
                ( lastUndoData, newUndoData ) => {
                    return lastUndoData.restoreData && newUndoData.restoreData
                        && (lastUndoData.restoreData.primaryType === newUndoData.restoreData.primaryType)
                        && (lastUndoData.actionString === newUndoData.actionString);
                }
        } );
    }

    /**
     * Make a close box for the reference line.
     * Caller must position the attribute.
     * @returns {*}
     */
    makeCloseBox( ) {
        let Dyn = this.dyn,
            plotElement = this,
            tooltipAndAriaLabel = Dyn.loc( 'remove Ruler on {{axis}}', { axis: this.isOnX ? 'X':'Y' } ),
            closebox = Dyn.plotUtil.makeCloseBox( this.plotShaper.adornments.paper,
                buttonHoverIn, buttonHoverOut, clickedOnClosebox,
                tooltipAndAriaLabel, Dyn.plotUtil.rulerCloseButtonId),
            cover = closebox.getCoverElement();
        this.plotShaper.setAdornmentLayer( 2, closebox );
        return closebox;

        function buttonHoverIn( e ) {
            cover.attr({ fill: Dyn.plotUtil.axisButtonBgDownColor });
            this.attr( { fill: Dyn.plotUtil.axisButtonDownColor, stroke: "none" } );
            if( this.data("tooltip"))
                Dyn.toolTip.show( this, event, { x:this.getBBox().x2, y:this.getBBox().y2 });
        }
        function buttonHoverOut( e ) {
            cover.attr({ fill: Dyn.plotUtil.axisButtonBgUpColor });
            this.attr( { fill: Dyn.plotUtil.axisButtonUpColor, stroke: "none" } );
            if( Dyn.toolTip.isMyElement( this ))
                Dyn.toolTip.hide();
        }
        function clickedOnClosebox( e ) {
            plotElement.handleDeleteRuler();
            e.stopPropagation(); // prevents event going to leaflet/map layer
            e.preventDefault();
        }
    }

    /**
     * Make a context menu for currently selected reference line.
     * Caller must position the menu button.
     * @returns {*} -- Raphael set with visible elements of button
     */
    makeContextMenuButton( ) {
        let Dyn = this.dyn,
            plotElement = this,
            tooltipAndAriaLabel = Dyn.loc( "Ruler menu" ),
            buttonSet = Dyn.plotUtil.makeContextMenuButton(
                this.plotShaper.adornments.paper,
                buttonClick,
                null, // keyDownHandler
                tooltipAndAriaLabel,
                Dyn.plotUtil.rulerContextMenuID );
        this.plotShaper.setAdornmentLayer( 2, buttonSet );
        return buttonSet;

        function buttonClick( e ) {
            let symbolElement = this,
                coverElement = buttonSet.getCoverElement(),
                model = plotElement.model,
                xy = Dyn.plotUtil.clickOrKeyboardEventToViewportXY( e, symbolElement ),
                props = {
                    colorHex: model.color,
                    colorsArray: plotElement.kLineColors,
                    isAbs: plotElement.model.isAbs,
                },
                handleChangeFunc = ( changeType, data ) => {
                    switch( changeType ) {
                        case 'rulerColor': // change the color of the ruler
                            if( data?.hex && data.hex !== model.color ) {
                                Dyn.assert( typeof data.hex === 'string' );
                                model.setColor( data.hex ); // set the color of the ruler
                                Dyn.mediator.publish( "plot:updateAdornment", {} ); // to update color in Summary View
                            }
                            break;
                        case 'isAbs': {  // set the absolute value flag
                                Dyn.assert( model.isAbs !== data.isAbs );
                                model.setIsAbs( data.isAbs );
                                Dyn.mediator.publish( "plot:updateAdornment", {} ); // to update value in Summary View
                            }
                            break;
                        case 'flip': {
                                model.flip(); // flip the ruler endpoints
                                Dyn.mediator.publish( "plot:updateAdornment", {} ); // to update value in Summary View
                            } break;
                        default: {
                            let notDeleted = !! plotElement.getAdornmentsEl( plotElement.getAdornmentsArray(), 'rulerSet' );
                            if( notDeleted ) {
                                Dyn.plotUtil.setAsKeyboardFocus( coverElement ); // on exit, set focus menu button
                            }
                        }
                    }
                };
            setTimeout(() => { // delayed call is required to show when e.type==='keykdown', reason unknown
                ContextMenuUtil.show( {x:xy.x, y:xy.y}, props, handleChangeFunc, Dyn.plotUtil.rulerContextMenuID, Dyn);
            }, 100 );
        }
    };

    handleDeleteRuler() {
        let Dyn = this.dyn,
            plotElementID = this.isOnX ? Dyn.EPlotControl.kControlsRulerX : Dyn.EPlotControl.kControlsRulerY;
        Dyn.plotView.removePlot( plotElementID );
    }

    /** add key handlers for the start/end lines and arrow/text of the ruler */
    addKeyHandlers( elementToFocus, ariaLabel, dragMove, dragStart, dragEnd ) {
        let Dyn = this.dyn,
            plotElement = this;
        Dyn.plotUtil.setKeyboardFocusable( elementToFocus, keyDownHandler.bind( elementToFocus ), ariaLabel );

        function keyDownHandler( e ) {
            //console.log( "keyDownHandler keydown:", e.key );
            switch( e.key ) { // const {key} = event; ES6+
                case "Delete":
                case "Backspace":
                    plotElement.handleDeleteRuler();
                    break;
                case "Enter":
                    plotElement.selectAdornment();
                    break;
                case "ArrowLeft":
                case "ArrowRight":
                case "ArrowUp":
                case "ArrowDown": {   // move this element one pixel in arrow's direction
                        let dist = Dyn.plotUtil.arrowKeyAcceleratedDistance( e.key ),
                            x, y /* placeholders -- x,y not used by drag handlers */,
                            element = this; // e.g.: line1 of dragLineMove.bind(line1)
                        dragStart.call( element, x, y, e );
                        dragMove.call( element, dist.dx, dist.dy, x, y, e );
                        dragEnd.call( element, e );
                    }
                    break;
            }
        }
    }

    /** Does this element have a selected line with the given ID?  */
    isAdornmentSelected( tableSetID, adornmentID ) {
        return( (tableSetID === this.setID) && (adornmentID === this.id) && this.isSelected );
    }

    /**
     * Select or deselect the adornment for this plot element, in this case the ruler line.
     * @param adornmentIndex - if 0, select the ruler; if -1 deselect the ruler
     * @param [data] - if defined, the IDs of the line to select
     */
    selectAdornment( adornmentIndex=0, data=null ) {
        let oldSelection = this.isSelected;
        if( adornmentIndex===0 || (data?.tableSetID === this.setID && data?.lineID === this.id) ) {
            this.isSelected = true;
            this.plotShaper.clearSelectedAdornments( this ); // clear other adornment selections
            this.updateSelectionVisuals();
        } else {
            this.clearSelectedAdornments();
        }
        return( oldSelection !== this.isSelected );
    }

    /**
     * {OVERRIDE} Deselect any selected adornments in this plot element.
     */
    clearSelectedAdornments() {
        let oldSelection = this.isSelected;
        this.isSelected = false;
        this.updateSelectionVisuals();
        return( oldSelection !== this.isSelected );
    };

    /** update the visuals for the selected ruler */
    updateSelectionVisuals() {
        let Dyn = this.dyn,
            rulerAdorns = this.getAdornmentsArray(),
            closeBox = this.getAdornmentsEl( rulerAdorns, 'closeBox' ),
            contextMenuButton = this.getAdornmentsEl( rulerAdorns, 'contextMenuButton' ),
            rulerSet = this.getAdornmentsEl( rulerAdorns, 'rulerSet' ),
            arrowPathEl = this.getSetEL( rulerSet, 'arrowPathEl'), // arrow for ruler
            textRectEl = this.getSetEL( rulerSet, 'textRectEl'); // text rectangle for ruler;
        if( this.isSelected && textRectEl ) {
            let wantVisible = !( Dyn.preferences.isViewOnly()),
                iconSize = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize ),
                refBBox = textRectEl.data("hasRulerText") ? textRectEl.getBBox() : arrowPathEl.getBBox(),
                pos = this.model.getButtonPositions( refBBox, iconSize, this.isOnX ),
                // wider clip area: or just don't use a clip-rect?
                clipRectString = Dyn.plotView.getPlotInteriorClipRect( pos.clipAreaMargin, pos.clipAreaMargin );

            closeBox.attr( 'clip-rect', clipRectString );
            closeBox.transform( "t" + pos.closeBox.x + "," + pos.closeBox.y );
            Dyn.plotUtil.showOrHideElement( closeBox, wantVisible );

            contextMenuButton.attr( 'clip-rect', clipRectString );
            contextMenuButton.transform( "t" + pos.contextMenuButton.x + "," + pos.contextMenuButton.y );
            Dyn.plotUtil.showOrHideElement( contextMenuButton, wantVisible );
        } else {
            // hide adornments that are shown during selection
            if( closeBox && Dyn.plotUtil.isElementVisible( closeBox )) {
                closeBox.hide();
                contextMenuButton.hide();
            }
        }
    }

    /**
     * Add a measure to display in the Summary View, as part of the "movable line" table.
     * @param rulerViewData
     */
    updateMeasures( rulerViewData ) {
        let Dyn = this.dyn,
            plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType );
        const setID = this.setID; // ID for the set of measures that form a Summary View table, e.g. 'ruler'
        const lineID = this.id; // unique ID for each row of the ruler table, e.g. 'X' or 'Y'
        clearOldMeasures();
        if( rulerViewData.string.value ) {
            addOneMeasure( Dyn.loc( this.model.isAbs ? "Length" : "Difference"), rulerViewData.string.value );
            addOneMeasure( Dyn.loc( this.model.isAbs ? "End" : "Begin" ), rulerViewData.string.c1 );
            addOneMeasure( Dyn.loc( "End" ), rulerViewData.string.c2 );
            addOneMeasure( plotMeasures.getNameOfColorMeasure(), this.model.color );
        }

        function clearOldMeasures() {
            measuresArray.length = 0; // remove old measures (values for summary table)
        }

        function addOneMeasure( name, value ) {
            // NOTE: coordinate with PlotMeasures.knownSetIDs, .tableTitles, .setIDsWithMeasuresInColumns,
            // and with RulerElementBase.isAdornmentSelected(), selectAdornment()
            let oneMeasure = {
                setID: setID, lineID: lineID, // table and row IDs
                measureName: name, value: value  }; // columns of the table
            plotMeasures.addToMeasuresArray( measuresArray, oneMeasure );
        }
    }

} // class RulerElement

/** controller for a ruler on the X-axis of the plot.*/
class RulerXElement extends RulerBaseElement {
    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityRuler,
            primaryType: Dyn.EPlotControl.kControlsRulerX,
            replaceType: Dyn.EPlotControl.kControlsRulerX,
            otherControls: []
        }, Dyn );
    }
    get id() { return 'X'; } // only one ruler on the X-axis, so use a single ID
    get isOnX() { return true; }
}

/** controller for a ruler on the Y-axis of the plot.*/
class RulerYElement extends RulerBaseElement {
    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityRuler,
            primaryType: Dyn.EPlotControl.kControlsRulerY,
            replaceType: Dyn.EPlotControl.kControlsRulerY,
            otherControls: []
        }, Dyn );
    }
    get id() { return 'Y'; } // only one ruler on the Y-axis, so use a single ID
    get isOnX() { return false; }
}

// export the model for use in other parts of the code
export const wrapRulerElement = function(Dyn) {
    Dyn.RulerXElement = RulerXElement;
    Dyn.RulerYElement = RulerYElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsRulerX, Dyn.RulerXElement );
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsRulerY, Dyn.RulerYElement );
}
